import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Plus, Wallet, Pencil, Trash2, Calculator, Search, X } from "lucide-react";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import BankCheckDialog, { groupByHolder } from "./BankCheckDialog";

export default function BanksPanel() {
  const [data, setData] = useState({ banks: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [checkOpen, setCheckOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [initialBalance, setInitialBalance] = useState("");
  const [accountHolder, setAccountHolder] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/banks");
      setData(res.data);
    } catch {
      toast.error("Erro ao carregar bancas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setName("");
    setInitialBalance("");
    setAccountHolder("");
    setNotes("");
    setDialogOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setName(b.name);
    setInitialBalance(String(b.initial_balance));
    setAccountHolder(b.account_holder || "");
    setNotes(b.notes || "");
    setDialogOpen(true);
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error("Informe o nome da banca");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        initial_balance: parseFloat(initialBalance) || 0,
        account_holder: accountHolder.trim() || null,
        notes: notes || null,
      };
      if (editing) {
        await api.put(`/banks/${editing.id}`, payload);
        toast.success("Banca atualizada");
      } else {
        await api.post("/banks", payload);
        toast.success("Banca cadastrada");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (id) => {
    try {
      await api.delete(`/banks/${id}`);
      toast.success("Banca removida");
      load();
    } catch {
      toast.error("Erro ao remover");
    }
  };

  const q = search.trim().toLowerCase();
  const filteredBanks = q
    ? data.banks.filter(
        (b) => b.name.toLowerCase().includes(q) || (b.account_holder || "").toLowerCase().includes(q)
      )
    : data.banks;

  if (loading) {
    return <div className="h-40 animate-pulse rounded-xl border border-white/10 bg-[#121212]" />;
  }

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">Minhas bancas</h2>
          <p className="text-xs text-zinc-500">
            Casas onde você mantém saldo, agrupadas por titular.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="check-banks-btn"
            onClick={() => setCheckOpen(true)}
            disabled={data.banks.length === 0}
            variant="outline"
            className="h-10 gap-2 rounded-full border-white/15 bg-transparent px-5 text-sm font-semibold text-zinc-200 hover:bg-white/5 hover:text-white disabled:opacity-40"
          >
            <Calculator className="h-4 w-4 text-[#D4FF00]" />
            Conferir bancas
          </Button>
          <Button
            data-testid="new-bank-btn"
            onClick={openNew}
            className="h-10 gap-2 rounded-full bg-[#D4FF00] px-5 font-bold text-black hover:bg-[#c4ee00]"
            style={{ transition: "background-color 0.2s ease" }}
          >
            <Plus className="h-4 w-4" />
            Nova banca
          </Button>
        </div>
      </div>

      {/* Search */}
      {data.banks.length > 0 && (
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="bank-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar banca ou titular..."
            className="h-10 bg-[#121212] pl-9 pr-9"
          />
          {search && (
            <button
              data-testid="bank-search-clear"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-zinc-500 hover:bg-white/5 hover:text-white"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {/* List */}
      {data.banks.length === 0 ? (
        <div
          data-testid="banks-empty"
          className="flex flex-col items-center justify-center rounded-xl border border-dashed border-white/10 bg-[#121212] p-16 text-center"
        >
          <Wallet className="mb-3 h-10 w-10 text-zinc-700" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-bold text-white">Nenhuma banca cadastrada</h3>
          <p className="mt-1 max-w-sm text-sm text-zinc-500">
            Cadastre suas casas de aposta para organizar por titular e fazer conferências.
          </p>
        </div>
      ) : filteredBanks.length === 0 ? (
        <div data-testid="banks-search-empty" className="rounded-xl border border-dashed border-white/10 bg-[#121212] p-10 text-center text-sm text-zinc-500">
          Nenhuma banca encontrada para "{search}".
        </div>
      ) : (
        <Accordion
          type="multiple"
          key={search ? "search" : "all"}
          defaultValue={search ? groupByHolder(filteredBanks).map(([h]) => h) : undefined}
          className="space-y-3"
        >
          {groupByHolder(filteredBanks).map(([holder, banks]) => (
            <AccordionItem
              key={holder}
              value={holder}
              data-testid={`holder-accordion-${holder}`}
              className="overflow-hidden rounded-xl border border-white/10 bg-[#121212]"
            >
              <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-white/[0.02]">
                <div className="flex flex-1 items-center gap-3 pr-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full border border-[#D4FF00]/30 bg-[#D4FF00]/10 text-[0.75rem] font-bold text-[#D4FF00]">
                    {holder.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="text-left">
                    <div className="font-display text-base font-bold text-white">{holder}</div>
                    <div className="text-[0.7rem] uppercase tracking-widest text-zinc-500">
                      {banks.length} banca{banks.length > 1 ? "s" : ""}
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="border-t border-white/5 bg-black/20 px-3 py-3 sm:px-5 sm:py-4">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {banks.map((b) => (
                    <div
                      key={b.id}
                      data-testid={`bank-card-${b.id}`}
                      className="group flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-[#121212] px-4 py-3 hover:border-white/25"
                      style={{ transition: "border-color 0.2s ease" }}
                    >
                      <div className="min-w-0">
                        <div className="truncate font-display text-base font-bold text-white">{b.name}</div>
                        <div className="truncate text-[0.75rem] text-zinc-500">{b.account_holder?.trim() || "Sem titular"}</div>
                      </div>
                      <div className="flex shrink-0 gap-1 md:opacity-0 md:group-hover:opacity-100" style={{ transition: "opacity 0.2s ease" }}>
                        <Button
                          data-testid={`edit-bank-${b.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(b)}
                          className="h-7 w-7 text-zinc-500 hover:bg-white/5 hover:text-white"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          data-testid={`delete-bank-${b.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => setConfirmId(b.id)}
                          className="h-7 w-7 text-zinc-500 hover:bg-red-500/10 hover:text-red-400"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      <BankCheckDialog open={checkOpen} onOpenChange={setCheckOpen} banks={data.banks} />

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="bank-form-dialog" className="border-white/10 bg-[#121212]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold text-white">
              {editing ? "Editar banca" : "Nova banca"}
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Registre o nome da casa e o titular da conta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">
                Nome da banca
              </Label>
              <Input
                data-testid="bank-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Bet365, Betano, Pinnacle..."
                className="bg-black/40"
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">
                Titular da conta
              </Label>
              <Input
                data-testid="bank-holder-input"
                value={accountHolder}
                onChange={(e) => setAccountHolder(e.target.value)}
                placeholder="Ex: João Silva, Esposa, Amigo..."
                className="bg-black/40"
              />
              <p className="mt-1 text-[0.7rem] text-zinc-500">
                Usado para agrupar as bancas por titular na aba.
              </p>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">
                Observações (opcional)
              </Label>
              <Textarea
                data-testid="bank-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ex: Bônus de boas-vindas de R$ 200..."
                className="min-h-[70px] bg-black/40"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              className="text-zinc-400 hover:text-white"
            >
              Cancelar
            </Button>
            <Button
              data-testid="save-bank-btn"
              onClick={save}
              disabled={saving}
              className="bg-[#D4FF00] font-bold text-black hover:bg-[#c4ee00]"
              style={{ transition: "background-color 0.2s ease" }}
            >
              {saving ? "Salvando..." : editing ? "Atualizar" : "Cadastrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent className="border-white/10 bg-[#121212]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Remover banca?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              A banca será removida. As entradas ligadas a essa casa continuarão registradas no histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-transparent text-zinc-300 hover:bg-white/5 hover:text-white">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-bank-btn"
              onClick={() => {
                doDelete(confirmId);
                setConfirmId(null);
              }}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
